import Axios from "axios";
import Cookies from "universal-cookie";
import { toast } from "react-toastify";
import { showLoading } from "react-global-loading";

const cookies = new Cookies();

// Support both build-time and runtime environment variables
// In Docker environment, frontend connects to backend via exposed host port
// Using 127.0.0.1 instead of localhost to avoid IPv6 resolution issues
const baseUrl = (window.env?.API_URL ||
                process.env.REACT_APP_API_URL ||
                "http://127.0.0.1:3001") + "/api";
const tokenName = window.env?.API_TOKEN_NAME || process.env.REACT_APP_API_TOKEN_NAME || "udatxu";
const loginPath = "/auth/sign-in/";
const dashboardPath = "/dashboard/";

const DEFAULT_CONTENT_TYPE = "application/json";

let api = Axios.create({
    baseURL: baseUrl,
    timeout: 30000, // 30 seconds for Argon2 hashing + email sending
});

function post(url, data, config="") {
    const ct = config.length === 0 ? DEFAULT_CONTENT_TYPE : config;
    return api.post(url, data, { headers: { "Content-Type": ct } });
}

function get(url, data={}, config="") {
    const ct = config.length === 0 ? DEFAULT_CONTENT_TYPE : config;
    return api.get(url, { headers: { "Content-Type": ct } });
}

function put(url, data, config="") {
    const ct = config.length === 0 ? DEFAULT_CONTENT_TYPE : config;
    return api.put(url, data, { headers: { "Content-Type": ct } });
}

function del(url, config="") {
    const ct = config.length === 0 ? DEFAULT_CONTENT_TYPE : config;
    return api.delete(url, { headers: { "Content-Type": ct } });
}

function checkToken() {
    const _token = cookies.get(tokenName);
    if (_token) {
        return true;
    }
    return false;
}
function getToken() {return cookies.get(tokenName);}
function setToken(tokenValue) {
    cookies.set(tokenName, tokenValue, { path: "/" });
}
function removeToken() {cookies.remove(tokenName, { path: "/" });}

function checkUser() {return localStorage.getItem("user");}
function setUser(user) {localStorage.setItem("user", JSON.stringify(user));}
function removeUser() {localStorage.removeItem("user");}
function getUser() {
    const blankData = { name: "", email: "", role: "" };
    if (checkUser() === null) return blankData;
    return checkUser() && JSON.parse(localStorage.getItem("user"));
}

function getProfilePictureUrl(user) {
    if (user?.profile_picture) {
        return user.profile_picture;
    }
    return null;
}

function checkUserActive() {
    const _token = cookies.get(tokenName);
    const _user = getUser();
    // User is considered active only if:
    // 1. Token exists
    // 2. User data exists
    // 3. User status is "active"
    if (_token && _user && _user.user_status === "active") {
        return true;
    }
    return false;
}

// Check if user is logged in but inactive (needs activation)
function isUserInactive() {
    const _token = cookies.get(tokenName);
    const _user = getUser();
    // User is inactive if:
    // 1. Token exists (logged in)
    // 2. User data exists
    // 3. User status is NOT "active"
    if (_token && _user && _user.user_status && _user.user_status !== "active") {
        return true;
    }
    return false;
}


api.interceptors.request.use((config) => {
    const _token = getToken();

    if (_token) {
        config.headers.Authorization = `Bearer ${_token}`;
    }
    // Content-Type is set per-request by post/get/put/del functions
    // Do NOT override it here — multipart uploads rely on their own Content-Type boundary

    // Show loading indicator (except for /deadline endpoint)
    if (config.url !== '/deadline') {
        showLoading(true);
    }
    
    return config;
}, (error) => {
    return Promise.reject(error);
});

api.interceptors.response.use(
    (response) => {
        // Hide loading spinner
        showLoading(false);

        // Note: Success toasts are now handled manually by individual components
        // This gives better control over notification messages and timing
        // Removed automatic success toast to prevent duplicate notifications

        return response;
    },
    (error) => {
        // Hide loading spinner
        showLoading(false);
        
        // Show error toast for API errors
        // Ignore /deadline endpoint errors
        if (error.config?.url !== '/deadline') {
            if (error.response) {
                // Server responded with error status
                const errorMessage = error.response.data?.message || error.response.data?.error || 'An error occurred';
                toast.error(errorMessage, {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
            } else if (error.request) {
                // Request made but no response
                toast.error('No response from server. Please check your connection.', {
                    position: "top-right",
                    autoClose: 5000,
                });
            } else {
                // Other errors
                if (error.message !== "Error Processing Token") {
                    toast.error(error.message, {
                        position: "top-right",
                        autoClose: 5000,
                    });
                }
            }
        }

        // Handle token errors
        if (error.message === "Error Processing Token") {
            removeToken();
            removeUser();
            window.location.href = loginPath;
        }
        return Promise.reject(error);
    }
);

export {
    get,
    post,
    put,
    del,
    baseUrl,
    loginPath,
    dashboardPath,
    tokenName,
    checkToken,
    checkUserActive,
    isUserInactive,
    getToken,
    setToken,
    removeToken,
    checkUser,
    setUser,
    removeUser,
    getUser,
    getProfilePictureUrl
};

export default api;